import { ApiError } from "./ApiError.js";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

async function fetchApi(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const config = {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      // 2026-07-04 tools-wide audit Tier 1 #1 : prefer the envelope's
      // `error` string, then fall back to the express-validator
      // `errors` array's first message (older responses / any future
      // envelope drift), and only then the generic. The generic
      // fallbacks are plain sentences (operator ruling 2026-08-27 : a
      // customer reads sentences, UPPERCASE is for the logs) - they
      // render verbatim in every tool's red Alert.
      throw new ApiError(
        data?.error ||
          data?.errors?.[0]?.msg ||
          "Something went wrong. Please try again.",
        response.status,
        data
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      "The request could not reach the server. Please check your connection and try again.",
      0,
      null
    );
  }
}

// Re-export ApiError for backwards compatibility
export { ApiError };

export const api = {
  // CSR endpoints
  generateCSR: (data) =>
    fetchApi("/csr/generate", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  decodeCSR: (csr) =>
    fetchApi("/csr/decode", {
      method: "POST",
      body: JSON.stringify({ csr }),
    }),

  // Chain endpoints
  analyzeChain: (certificate) =>
    fetchApi("/chain/analyze", {
      method: "POST",
      body: JSON.stringify({ certificate }),
    }),

  analyzeChainByUrl: (url) =>
    fetchApi("/chain/analyze-url", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),

  // Match endpoints
  verifyMatch: (certificate, privateKey) =>
    fetchApi("/match/verify", {
      method: "POST",
      body: JSON.stringify({ certificate, privateKey }),
    }),

  // PFX endpoints
  convertToPFX: (certificate, privateKey, password, encryption) =>
    fetchApi("/pfx/convert", {
      method: "POST",
      body: JSON.stringify({ certificate, privateKey, password, encryption }),
    }),

  // CAA endpoints
  generateCAA: (data) =>
    fetchApi("/caa/generate", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  validateCAA: (record) =>
    fetchApi("/caa/validate", {
      method: "POST",
      body: JSON.stringify({ record }),
    }),

  checkCAA: (domain) =>
    fetchApi("/caa-checker/check", {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),

  // DCV Checker endpoints
  checkDCV: (data) =>
    fetchApi("/dcv/check", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // DNS Stamp endpoints
  generateDNSStamp: (data) =>
    fetchApi("/dnsstamp/generate", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  decodeDNSStamp: (stamp) =>
    fetchApi("/dnsstamp/decode", {
      method: "POST",
      body: JSON.stringify({ stamp }),
    }),

  getDNSProtocols: () =>
    fetchApi("/dnsstamp/protocols", {
      method: "GET",
    }),
};

export default api;
